import React, {useEffect, useState, useRef} from 'react';
import {connect} from "react-redux";
import {useNavigate} from "react-router-dom";

import FreakPreview from './FreakPreview';
import useTranslation from "../../customHooks/translations";
import {DEFAULT_BORDER_COLOR, DEFAULT_THEME_COLOR} from "../../constants/theme";
import Button from "@mui/material/Button";
import RssFeedIcon from '@mui/icons-material/RssFeed';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import InfiniteScroll from "react-infinite-scroller";
import agent from "../../agent";
import {FREAKS_SET_PAGE} from "../../constants/actionTypes";
import CommonLoader from "../Loaders/CommonLoader";

const FreakList = props => {
  const translation = useTranslation();
  const navigate = useNavigate();
  const [freaks, setFreaks] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const pageRef = useRef(0);
  const loadingRef = useRef(false);

  useEffect(() => {
    pageRef.current = 0;
    loadingRef.current = false;
    setFreaks([]);
    setHasMore(true);
    setIsLoading(true);

    loadMoreFreaks();
  }, [
    props.tab,
    props.context,
    props.latitudeFilter,
    props.longitudeFilter,
    props.distanceFilter,
    props.sortByLikesFilter,
    props.sortByDateFilter,
    props.filterByNameFilter,
    props.filterByDateFromFilter,
    props.filterByDateToFilter,
    props.filterByStatusFilter,
    props.filterByParticipantFilter,
  ]);

  const loadMoreFreaks = async () => {
    // Synchronous re-entrancy lock: react-infinite-scroller can fire loadMore
    // again while a fetch is still in flight (state-based guards are stale
    // within the same tick), which would load the same page twice.
    if (loadingRef.current || !hasMore) {
      return;
    }
    loadingRef.current = true;

    try {
      setIsLoading(true);

      const page = pageRef.current;
      let results = { freaks: [] };

      if (!props.tab) {
        results = await agent.Freaks.feed(
          page,
          props.context,
          props.device,
          props.latitudeFilter,
          props.longitudeFilter,
          props.distanceFilter,
          props.sortByLikesFilter,
          props.sortByDateFilter,
          props.filterByNameFilter,
          props.filterByDateFromFilter,
          props.filterByDateToFilter,
          props.filterByStatusFilter,
          props.filterByParticipantFilter,
          props.profileUsername || props.currentUser.username
        );
      } else if (props.tab === 'daily') {
         results = await agent.Feeds.daily(
           page,
           props.context,
           props.device,
           props.latitudeFilter,
           props.longitudeFilter,
           props.distanceFilter,
           props.sortByLikesFilter,
           props.sortByDateFilter,
           props.filterByNameFilter,
           props.filterByDateFromFilter,
           props.filterByDateToFilter,
           props.filterByStatusFilter,
           props.filterByParticipantFilter,
           props.currentUser.username,
         );
      } else if (props.tab === 'favourites') {
        results = await agent.Freaks.favoritedBy(props.profileUsername, page, props.context, props.device)
      } else if (props.tab ==='forFreakHub') {
        results = await agent.Freaks.forFreakHub(
          page,
          props.context,
          props.device,
          props.forFreakHub,
        );
      } else {
        results = await agent.Freaks[props.tab](
          page,
          props.context,
          props.device,
          props.latitudeFilter,
          props.longitudeFilter,
          props.distanceFilter,
          props.sortByLikesFilter,
          props.sortByDateFilter,
          props.filterByNameFilter,
          props.filterByDateFromFilter,
          props.filterByDateToFilter,
          props.filterByStatusFilter,
          props.filterByParticipantFilter,
          props.currentUser.username,
        );
      }

      setFreaks(prev => [...prev, ...results.freaks]);
      pageRef.current += 1;

      if (results?.freaks?.length === 0) {
        setHasMore(false);
      }
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  };

  if (isLoading === false && freaks?.length === 0 && !props.errorGeoLocation) {
    return (
      <div style={{
             minHeight: 150,
             display: 'flex',
             justifyContent: 'center',
             alignItems: 'center',
             textAlign: 'center',
          }}>
        <span style={{ fontSize: '15px' }}>
          {
            props.tab === 'daily'
             ? <Button
                sx={{
                  color: DEFAULT_THEME_COLOR,
                  borderColor: DEFAULT_BORDER_COLOR,
                  'z-index': '0',
                  marginLeft: 1,
                  marginRight: 1,
                  marginTop: 2,
                  marginBottom: 2,
                  '&:hover': {
                    color: DEFAULT_THEME_COLOR,
                    borderColor: DEFAULT_THEME_COLOR,
                  }
                }}
                variant="outlined"
                startIcon={<RssFeedIcon />}
                onClick={() => {
                  navigate('/settings');
                }}
              >
                {translation.freaks.emptyListDaily}
              </Button>
            : null
          }
          {
            props.tab === 'followed'
            ? <Button
                sx={{
                  color: DEFAULT_THEME_COLOR,
                  borderColor: DEFAULT_BORDER_COLOR,
                  'z-index': '0',
                  marginLeft: 1,
                  marginRight: 1,
                  marginTop: 2,
                  marginBottom: 2,
                  '&:hover': {
                    color: DEFAULT_THEME_COLOR,
                    borderColor: DEFAULT_THEME_COLOR,
                  }
                }}
                variant="outlined"
                startIcon={<PersonSearchIcon />}
                onClick={() => {
                  navigate('/top-freaks');
                }}
              >
                {translation.freaks.emptyListFollowed}
              </Button>
            : null
          }
          {
            props.tab !== 'daily' && props.tab !== 'followed'
             ? <Button
                sx={{
                  color: DEFAULT_THEME_COLOR,
                  borderColor: DEFAULT_BORDER_COLOR,
                  'z-index': '0',
                  marginLeft: 1,
                  marginRight: 1,
                  marginTop: 2,
                  marginBottom: 2,
                  '&:hover': {
                    color: DEFAULT_THEME_COLOR,
                    borderColor: DEFAULT_THEME_COLOR,
                  }
                }}
                variant="outlined"
              >
                {translation.freaks.emptyList}
              </Button>
             : null
          }
        </span>
      </div>
    );
  }

  return (
    <>
      <InfiniteScroll
        pageStart={0}
        initialLoad={false}
        loadMore={loadMoreFreaks}
        hasMore={hasMore}
        loader={
          <CommonLoader />
        }
      >
        {
          freaks?.map(freak => {
            return (
              <FreakPreview
                freak={freak}
                key={freak.slug}
              />
            );
          })
        }
      </InfiniteScroll>
    </>
  );
};

const mapStateToProps = state => ({
  ...state.freakList,
  device: state.common.device,
  currentUser: state.common.currentUser,
  distanceFilter: state.common.distanceFilter,
  sortByLikesFilter: state.common.sortByLikesFilter,
  sortByDateFilter: state.common.sortByDateFilter,
  filterByNameFilter: state.common.filterByNameFilter,
  filterByDateFromFilter: state.common.filterByDateFromFilter,
  filterByDateToFilter: state.common.filterByDateToFilter,
  filterByStatusFilter: state.common.filterByStatusFilter,
  filterByParticipantFilter: state.common.filterByParticipantFilter,
  latitudeFilter: state.common.latitudeFilter,
  longitudeFilter: state.common.longitudeFilter,
});

const mapDispatchToProps = dispatch => ({
  onSetPage: (page, payload) =>
    dispatch({ type: FREAKS_SET_PAGE, page, payload })
});

export default connect(mapStateToProps, mapDispatchToProps)(FreakList);
